import clsx from "clsx";
import { Link, useNavigation, useResolvedPath, type To } from "react-router";
import {
  ComponentPropsWithoutRef,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
} from "react";

function usePendingNavigation(to: To | undefined): {
  isPending: boolean;
  markTapped: () => void;
} {
  const navigation = useNavigation();
  const resolved = useResolvedPath(to ?? ".");
  const wasTappedRef = useRef(false);

  useEffect(() => {
    if (navigation.state === "idle") wasTappedRef.current = false;
  }, [navigation.state]);

  const isMatchingNav =
    to !== undefined &&
    navigation.state !== "idle" &&
    navigation.location?.pathname === resolved.pathname;

  return {
    isPending: isMatchingNav && wasTappedRef.current,
    markTapped: () => {
      wasTappedRef.current = true;
    },
  };
}

// ============================================
// Types
// ============================================

export type ButtonTheme = "red" | "green" | "cream" | "cream-light" | "white";

type BaseButtonProps = {
  theme?: ButtonTheme;
  loading?: boolean;
  className?: string;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: React.ReactNode;
};

type ButtonAsButton = BaseButtonProps &
  Omit<ComponentPropsWithoutRef<"button">, keyof BaseButtonProps> & {
    to?: undefined;
  };

type ButtonAsLink = BaseButtonProps &
  Omit<ComponentPropsWithoutRef<typeof Link>, keyof BaseButtonProps> & {
    to: ComponentPropsWithoutRef<typeof Link>["to"];
  };

export type PrimaryButtonProps = ButtonAsButton | ButtonAsLink;
export type SecondaryButtonProps = ButtonAsButton | ButtonAsLink;

// ============================================
// Loading Spinner Component
// ============================================

const LoadingSpinner = ({ theme }: { theme: ButtonTheme }) => (
  <div role="status" className="absolute w-full h-full">
    <div
      className={clsx(
        "py-4 items-center justify-center flex w-full h-full rounded-lg",
        theme === "white" && "bg-butter-green/30",
        theme === "red" && "bg-butter-cream/20"
      )}
    >
      <svg
        aria-hidden="true"
        className={clsx(
          "w-6 h-6 text-butter-cream-light animate-spin fill-butter-green",
          theme === "white" && "fill-butter-green text-butter-cream-light",
          theme === "red" && "fill-butter-cream-light text-butter-cream-light/20"
        )}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    </div>
    <span className="sr-only">Loading...</span>
  </div>
);

// ============================================
// Primary Button
// ============================================

export const PrimaryButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  PrimaryButtonProps
>(function PrimaryButton(
  {
    theme = "red",
    loading,
    className,
    children,
    disabled,
    leftIcon,
    rightIcon,
    ...props
  },
  ref
) {
  const { isPending, markTapped } = usePendingNavigation(props.to);
  const isLoading = loading || isPending;
  const content = (
    <>
      <span
        className={clsx(
          "inline-flex items-center justify-center gap-2 leading-tight",
          isLoading && "opacity-0"
        )}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </span>
      {isLoading && <LoadingSpinner theme={theme} />}
    </>
  );

  const classes = clsx([
    "inline-flex relative w-full items-center justify-center rounded-lg py-3 px-4 text-sm font-sans outline-none border-none appearance-none cursor-pointer transition-transform hover:scale-[1.03]",
    className,
    theme === "red" && "bg-butter-pink text-butter-cream-light",
    theme === "green" && "bg-butter-green text-butter-cream-light",
    theme === "cream" && "bg-butter-cream text-butter-green",
    theme === "cream-light" && "bg-butter-cream-light text-butter-green",
    theme === "white" && "bg-white text-butter-green",
    disabled && "opacity-50 !cursor-default hover:!scale-100",
  ]);

  if (props.to !== undefined && !disabled) {
    const { to, onClick, ...linkProps } = props as ButtonAsLink;
    return (
      <Link
        className={classes}
        to={to}
        ref={ref as never}
        onClick={(e) => {
          markTapped();
          onClick?.(e);
        }}
        {...linkProps}
      >
        {content}
      </Link>
    );
  }

  const { to: _to, ...buttonProps } = props as ButtonAsButton;
  return (
    <button
      className={classes}
      ref={ref as React.Ref<HTMLButtonElement>}
      disabled={disabled}
      {...buttonProps}
    >
      {content}
    </button>
  );
});

// ============================================
// Secondary Button
// ============================================

export const SecondaryButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  SecondaryButtonProps
>(function SecondaryButton(
  {
    theme = "red",
    loading,
    className,
    children,
    disabled,
    leftIcon,
    rightIcon,
    ...props
  },
  ref
) {
  const { isPending, markTapped } = usePendingNavigation(props.to);
  const isLoading = loading || isPending;
  const content = (
    <>
      <span
        className={clsx(
          "inline-flex items-center justify-center gap-2 leading-tight",
          isLoading && "opacity-0"
        )}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </span>
      {isLoading && <LoadingSpinner theme={theme} />}
    </>
  );

  const classes = clsx([
    "inline-flex relative w-full items-center justify-center rounded-lg py-3 px-4 text-sm font-sans font-bold outline-none appearance-none cursor-pointer transition-transform hover:scale-[1.03]",
    className,
    theme === "red" && "border-2 border-butter-pink text-butter-pink",
    theme === "green" && "border-2 border-butter-green text-butter-green",
    disabled && "opacity-50 !cursor-default hover:!scale-100",
  ]);

  if (props.to !== undefined && !disabled) {
    const { to, onClick, ...linkProps } = props as ButtonAsLink;
    return (
      <Link
        className={classes}
        to={to}
        ref={ref as never}
        onClick={(e) => {
          markTapped();
          onClick?.(e);
        }}
        {...linkProps}
      >
        {content}
      </Link>
    );
  }

  const { to: _to, ...buttonProps } = props as ButtonAsButton;
  return (
    <button
      className={classes}
      ref={ref as React.Ref<HTMLButtonElement>}
      disabled={disabled}
      {...buttonProps}
    >
      {content}
    </button>
  );
});
